import { isFunction } from '@mechhive/helpers';
import classNames from 'classnames';
import type { ChangeEvent, FocusEvent, FocusEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { Price } from '../Price';
import clsx from 'clsx';

type InputAmountSelectProps = {
  className?: string;
  value: number;
  defaultValue?: number;
  title?: string;
  name?: string;
  currency: string;
  required?: boolean;
  min?: number;
  max?: number;
  invalid?: boolean;
  onChange?: ( e: ChangeEvent<HTMLInputElement> ) => void;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
}

export const InputAmountSelect = ( { className, value, defaultValue, title, currency, onChange, required, min, max, name, invalid = false, onBlur }: InputAmountSelectProps ) => {
  const [ inputValue, setInputValue ] = useState<string>( defaultValue ? defaultValue.toString() : value.toString() );
  const [ inputNumber, setInputNumber ] = useState<number>( defaultValue ? defaultValue : value );
  const [ isFocus, setIsFocus ] = useState<boolean>( false );
  const [ isHover, setIsHover ] = useState<boolean>( false );
  
  const handleInputChange = ( e: ChangeEvent<HTMLInputElement>, initial: boolean = false ) => {
    setInputValue( e.target.value );

    const numberValue = parseFloat( e.target.value );
    if ( isNaN( numberValue ) ) {
      setInputNumber( 0 );
      setInputValue( '' );
    } else {
      setInputNumber( numberValue );
    }

    if ( isFunction( onChange ) ) {
      onChange( e );
    }
  }

  const handleFocus = ( e: FocusEvent<HTMLInputElement> ) => {
    setIsFocus( true );

    e.currentTarget.select();
  }

  const handleBlur = ( e ) => {
    setIsFocus( false );

    if ( isFunction( onBlur ) ) {
      onBlur( e );
    }
  }

  useEffect( () => {
    setInputValue( value.toString() );
    setInputNumber( value );
  }, [ value ] )

  return (
    <div className={ clsx(
      'flex flex-col gap-3',
      className
    ) }>
      { title &&
        <div className={ 'text-[#ABB0C7] text-left font-semibold' }>
          { title }
        </div>
      }
      <div className={ classNames(
        'relative border aria-[invalid="true"]:border-red-500 rounded-[10px] bg-[#0C102E]/50 shadow-[0px_-8px_20px_0px_rgba(255,255,255,0.20)_inset] transition-all duration-200',
        isFocus || isHover ? 'border-[#5A7DFB] shadow-[0px_4px_10px_0px_rgba(90,125,251,0.0),0px_-8px_20px_0px_rgba(90,125,251,0.20)_inset]' : 'border-[#7C7AFE]',
        isFocus ? 'shadow-[0px_4px_10px_0px_rgba(90,125,251,0.40),0px_-8px_20px_0px_rgba(90,125,251,0.20)_inset]' : 'shadow-[0px_4px_10px_0px_rgba(90,125,251,0.0),0px_-8px_20px_0px_rgba(255,255,255,0.20)_inset]'
      ) }>
        <div className={ classNames(
          'absolute inset-0 bg-transparent pointer-events-none flex items-center justify-center text-white transition-opacity duration-100',
          isFocus ? 'opacity-0' : 'opacity-100'
        ) }>
          <Price currency={ currency }>{ inputNumber }</Price>
        </div>
        <input
          className={ classNames(
            'w-full text-center  bg-transparent text-white rounded-[10px] transition-opacity duration-100 p-4  data-[size=small]:py-3 leading-[2px] data-[size=small]:leading-[18px] text-base font-light outline-none',
            isFocus ? 'opacity-100' : 'opacity-0'
          ) }
          name={ name }
          required={ required }
          value={ inputValue }
          type={ 'number' }
          min={ min }
          max={ max }
          step={ '0.01' }
          onChange={ handleInputChange }
          onFocus={ handleFocus } 
          onBlur={ handleBlur }
          onMouseEnter={ () => setIsHover( true ) }
          onMouseLeave={ () => setIsHover( false ) }
        />
      </div>
    </div>
  )
}
